// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.UserSetupDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.UserAccountConstants';
import uiState from 'logic/Izenda.UiState';
import ActionBar from 'modules/Settings/UserSetup/ActionBar/Izenda.ActionBar';
import Panel from 'share/components/controls/Izenda.Panel';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import Input from 'share/components/controls/Izenda.Input';
import Validator from 'share/components/hoc/Izenda.Validator';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import DropDown from 'share/components/controls/Izenda.DropDown';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';
import actions from './Izenda.UserSetUpDetailAction';
import izendaCfg from '../../../../Izenda.Config';
import { isNull, isEmpty } from 'lodash/fp';

const ValidatableInput = Validator()(Input);
const ValidatabSelect = Validator()(Select);

/**
 * UserAlertInfo component
 */
const UserAlertInfo = connect()(({ store }) => {
  const configUser = store.configUserCloned;
  const headerClass = classnames(
    'u_floatLeft u_fullWidth izenda-HeaderInformation',
    configUser.active ? 'izenda-HeaderActive' : 'izenda-HeaderInActive'
  );
  const className = classnames(
    'izenda-CustomHeader',
    configUser.active ? 'izenda-ActivateUser' : 'izenda-InactivateUser'
  );

  return (
    <GridLayout.Row className={classnames(configUser.isNew ? 'hidden' : null)}>
      <GridLayout.Col cols='12'>
        <div className={headerClass}>
          <i className='icon-attention' />
          <span className={className}>{configUser.inactiveReason}</span>
        </div>
      </GridLayout.Col>
    </GridLayout.Row>
  );
});

/**
 * UserSystemAdminInfo component
 */
@connect()
class UserSystemAdminInfo extends Component {
  /**
   * On change system admin
   * @param {any} e
   */
  onChangeSystemAdmin = (e: any) => {
    this.props.store.configUserCloned.updateSelectedSystemAdmin(e.target.checked);
  };
  render() {
    const { store, localization } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <label className='u_controlHeight control-label'>
            {localization.getText('USER_SETUP_LABEL_SYSTEM_ADMIN')}
          </label>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <Input type='checkbox' checked={configUser.systemAdmin} onChange={this.onChangeSystemAdmin} />
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserIdInfo component
 */
@connect()
class UserIdInfo extends Component {
  /**
   * On change user name
   * @param {any} e
   */
  onChangeUserName = (e: any) => {
    this.props.store.configUserCloned.updateUserName(e.target.value);
  };
  render() {
    const { store, localization, readOnly } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <label className='validation required u_controlHeight control-label'>
            {localization.getText('USER_SETUP_LABEL_USER_ID')}
          </label>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <ValidatableInput
            field='UserName'
            className='form-control'
            value={configUser.userName}
            validations={constants.USER_SETUP_CONSTRAINTS.USER_ID}
            onChange={this.onChangeUserName}
            readOnly={readOnly}
          />
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserFirstNameInfo component
 */
@connect()
class UserFirstNameInfo extends Component {
  /**
   * On change first name
   * @param {any} e
   */
  onChangeFirstName = (e: any) => {
    this.props.store.configUserCloned.updateFirstName(e.target.value);
  };
  render() {
    const { store, localization, readOnly } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <span className='validation required u_controlHeight control-label'>
            {localization.getText('USER_SETUP_LABEL_FIRST_NAME')}
          </span>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <ValidatableInput
            field='firstName'
            className='form-control'
            value={configUser.firstName}
            validations={constants.USER_SETUP_CONSTRAINTS.FIRST_NAME}
            onChange={this.onChangeFirstName}
            readOnly={readOnly}
          />
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserLastNameInfo component
 */
@connect()
class UserLastNameInfo extends Component {
  /**
   * On change last name
   * @param {any} e
   */
  onChangeLastName = (e: any) => {
    this.props.store.configUserCloned.updateLastName(e.target.value);
  };
  render() {
    const { store, localization, readOnly } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <span className='validation required u_controlHeight control-label'>
            {localization.getText('USER_SETUP_LABEL_LAST_NAME')}
          </span>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <ValidatableInput
            field='lastName'
            className='form-control'
            value={configUser.lastName}
            validations={constants.USER_SETUP_CONSTRAINTS.LAST_NAME}
            onChange={this.onChangeLastName}
            readOnly={readOnly}
          />
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserEmailInfo component
 */
@connect()
class UserEmailInfo extends Component {
  /**
   * On change email
   * @param {any} e
   */
  onChangeEmail = (e: any) => {
    this.props.store.configUserCloned.updateEmail(e.target.value);
  };
  render() {
    const { store, localization, readOnly } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <span className='u_controlHeight control-label'>{localization.getText('COMMON_LABEL_EMAIL')}</span>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <ValidatableInput
            className='form-control'
            value={configUser.emailAddress}
            onChange={this.onChangeEmail}
            readOnly={readOnly}
            field='emailAddress'
            validations={constants.USER_SETUP_CONSTRAINTS.EMAIL}
          />
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserRoleInfo component
 */
@connect()
class UserRoleInfo extends Component {
  /**
   * On change role
   * @param {any} value
   */
  onChangeRole = (value: any) => {
    this.props.store.configUserCloned.updateRole(value);
  };
  render() {
    const { store, localization, readOnly, disabled } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <span className='validation required u_controlHeight control-label'>
            {localization.getText('COMMON_LABEL_ROLE')}
          </span>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <ValidatabSelect
            multi
            simpleValue
            options={store.model.roleList}
            labelKey='name'
            valueKey='id'
            field='roles'
            noResultsText={localization.getText('COMMON_MESSAGES_NO_RESULT')}
            allowCreate={false}
            onChange={this.onChangeRole}
            value={configUser.rolesValue}
            validations={constants.USER_SETUP_CONSTRAINTS.ROLE}
            readOnly={readOnly}
            disabled={disabled}
          />
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserTimeZoneInfo component
 */
@connect()
class UserTimeZoneInfo extends Component {

  /**
   * Check if timzezone value is valid
   * @param {string} value  value to be validated
   * return {string}        empty if valid
   */
  validateTimezoneValue = (value: string):string => {
    const { localization } = this.props;
    return isEmpty(value) || /^[-\+]?\d+([\.,]\d+)?$/g.test(value || '')
      ? ''
      : localization.getText('COMMON_MESSAGES_INVALID_TIMEZONE');
  };

  /**
   * On change timezone data offset
   * @param {any} e
   */
  onChangeTimezoneDataOffset = (e: any) => {
    this.props.store.configUserCloned.updateTimezoneDataOffset(e.target.value);
  };

  /**
   * On click reset value timezone.
   */
  onClickResetValueTimeZone = () => {
    this.props.store.configUserCloned.resetValueTimeZone('dataOffset');
  };

  /**
   * On change timezone timestamp offset
   * @param {any} e
   */
  onChangeTimezoneTimestampOffset = (e: any) => {
    this.props.store.configUserCloned.updateTimezoneTimestampOffset(e.target.value);
  };

  /**
   * On click timezone timestamp offset
   */
  resetTimezoneTimestampOffset = () => {
    this.props.store.configUserCloned.resetValueTimeZone('timestampOffset');
  };
  render() {
    const { store, localization, readOnly } = this.props;
    const configUser = store.configUserCloned;
    const validateTimezoneValueAction = {
      action: value => this.validateTimezoneValue.bind(this, value)
    };
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <span className='u_controlHeight control-label'>{localization.getText('USER_SETUP_LABEL_TIME_ZONE')}</span>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <GridLayout.Col cols='12' className='u_noPaddingAll'>
            <GridLayout.Col cols='1' className='u_noPaddingAll'>
              <span className='u_controlHeight control-label'>
                {localization.getText('USER_SETUP_LABEL_DATA_OFFSET')}
              </span>
            </GridLayout.Col>
            <GridLayout.Col cols='2' className='u_noPaddingAll'>
              <ValidatableInput
                className='form-control'
                field='DataOffset'
                value={configUser.dataOffset}
                onChange={this.onChangeTimezoneDataOffset}
                validations={validateTimezoneValueAction}
                readOnly={readOnly}
              />
            </GridLayout.Col>
            <GridLayout.Col cols='1'>
              <i className='icon-ccw u_controlHeight control-label' onClick={this.onClickResetValueTimeZone} />
            </GridLayout.Col>
            <GridLayout.Col cols='2'>
              <span className='u_controlHeight control-label'>
                {localization.getText('USER_SETUP_LABEL_TIMESTAMP_OFFSET')}
              </span>
            </GridLayout.Col>
            <GridLayout.Col cols='2 u_noPaddingAll'>
              <ValidatableInput
                className='form-control'
                field='TimestampOffset'
                value={configUser.timestampOffset}
                onChange={this.onChangeTimezoneTimestampOffset}
                validations={validateTimezoneValueAction}
                readOnly={readOnly}
              />
            </GridLayout.Col>
            <GridLayout.Col cols='1'>
              <i className='icon-ccw u_controlHeight control-label' onClick={this.resetTimezoneTimestampOffset} />
            </GridLayout.Col>
            <GridLayout.Col cols='3' className='u_noPaddingAll' />
          </GridLayout.Col>
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

/**
 * UserRoleInfo component
 */
@connect()
class UserLanguageInfo extends Component {
  /**
   * On update language.
   * @param {any} value
   */
  onUpdateLanguage = (value: any) => {
    this.props.store.configUserCloned.updateLanguage(value);
  };

  /**
   * On update data format.
   * @param {any} value
   */
  onUpdateDateFormat = (value: any) => {
    this.props.store.configUserCloned.updateDateFormat(value);
  };

  render() {
    const { store, localization } = this.props;
    const configUser = store.configUserCloned;
    return (
      <GridLayout.Row>
        <GridLayout.Col cols='2'>
          <span className='u_controlHeight control-label'>{localization.getText('COMMON_LABEL_LANGUAGE')}</span>
        </GridLayout.Col>
        <GridLayout.Col cols='10'>
          <GridLayout.Col cols='3' className='u_noPaddingAll'>
            <DropDown
              options={store.model.languageList}
              labelField='language'
              valueField='cultureName'
              onChange={this.onUpdateLanguage}
              value={configUser.cultureName}
            />
          </GridLayout.Col>
          <GridLayout.Col cols='1' />
          <GridLayout.Col cols='2'>
            <span className='u_controlHeight control-label'>{localization.getText('COMMON_LABEL_DATE_FORMAT')}</span>
          </GridLayout.Col>
          <GridLayout.Col cols='3' className='u_noPaddingAll'>
            <DropDown
              options={store.model.dateFormatList}
              labelField='labelDateFormat'
              valueField='dateFormat'
              onChange={this.onUpdateDateFormat}
              value={configUser.dateFormat}
            />
          </GridLayout.Col>
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }
}

const UserSetupDetail = actions(({ store, authorizationConstants, onSave, onCancel, readOnly }) => {
  const permissionKeys = authorizationConstants.PERMISSION_KEY;
  const configUser = store.configUserCloned;
  const securityManager = new SecurityManager();
  const userRoleAssociation = securityManager.checkPrivileges([permissionKeys.UserSetupUserRoleAssociationValue ]);

  return (
    <div className='izenda-Wrapper'>
      <ActionBar store={store} onSave={onSave} onCancel={onCancel} readOnly={readOnly} />
      <div className='izenda-Wrapper-content izenda-UserSetupMainContent'>
        <ScrollBox>
          <div className='u_paddingLeftRight'>
            <Panel>
              <GridLayout>
                <UserAlertInfo store={store} />
                {isNull(uiState.getTenantId()) &&
                  uiState.currentUser.systemAdmin && <UserSystemAdminInfo store={store} />}
                <UserIdInfo store={store} readOnly={izendaCfg.Integration} />
                <UserFirstNameInfo store={store} />
                <UserLastNameInfo store={store} />
                <UserEmailInfo store={store} />
                {!configUser.systemAdmin && (
                  <UserRoleInfo disabled={!userRoleAssociation} store={store} />
                )}
                <UserTimeZoneInfo store={store} />
                <UserLanguageInfo store={store} />
              </GridLayout>
            </Panel>
          </div>
        </ScrollBox>
      </div>
    </div>
  );
});

export default UserSetupDetail;



// WEBPACK FOOTER //
// ./ui/modules/Settings/UserSetup/UserSetupDetail/Izenda.UserSetupDetail.js